import { useCallback, useEffect, useRef, useState } from 'react';
import { buildingsApi, PhaseAStatusResponse } from '../services/api';

/**
 * Zadanie 5.6 (2026-08-20): polling FAZY A — zadania w tle uruchamianego przy dodaniu budynku.
 *
 * Bliźniak `useCeebRegisterPolling` (ta sama rodzina: 202 → poll co 3 s → świeży terminal,
 * PEŁNY guard anty-stale na `finishedAt`), z jedną istotną różnicą: Faza A ma PIĘĆ KROKÓW
 * i pasek postępu musi je pokazywać NA BIEŻĄCO. Dlatego `result` jest aktualizowany także
 * w stanie `running` — a nie dopiero na terminalu, jak przy rejestracji.
 *
 * Guard anty-stale zostaje dokładnie tam, gdzie był potrzebny: przy uznaniu przebiegu za
 * ZAKOŃCZONY. Bez niego (a) `idle` między 202 a pierwszym zapisem subprocesu wyglądałby jak
 * koniec, (b) ponowne uruchomienie odczytałoby STARY terminal poprzedniego przebiegu.
 */
export type CeebPhaseAUiStatus = 'idle' | 'running' | 'done' | 'error';

export interface UseCeebPhaseAPolling {
  status: CeebPhaseAUiStatus;
  result: PhaseAStatusResponse | null;
  start: (buildingId: string) => Promise<void>;
  /** Podgląd trwającego/zakończonego przebiegu BEZ uruchamiania nowego (wejście na kartę budynku). */
  observe: (buildingId: string) => Promise<void>;
}

const POLL_INTERVAL_MS = 3000;
// Faza A robi do pięciu kroków z live-CEEB (login + DBC + przeładowania) — dłużej niż rejestracja.
const MAX_POLL_MS = 15 * 60 * 1000;

export function useCeebPhaseAPolling(): UseCeebPhaseAPolling {
  const [status, setStatus] = useState<CeebPhaseAUiStatus>('idle');
  const [result, setResult] = useState<PhaseAStatusResponse | null>(null);

  const pollRef = useRef<number | null>(null);
  const startedAtRef = useRef<number>(0);
  const previousFinishedAtRef = useRef<string | null | undefined>(undefined);

  const clearPoll = useCallback(() => {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => clearPoll, [clearPoll]);

  const startPolling = useCallback(
    (buildingId: string) => {
      clearPoll();
      pollRef.current = window.setInterval(async () => {
        if (Date.now() - startedAtRef.current >= MAX_POLL_MS) {
          clearPoll();
          setResult({ status: 'error', error: 'Faza A nie odpowiada — sprawdź logi bota i ponów' });
          setStatus('error');
          return;
        }
        try {
          const data = await buildingsApi.getPhaseAStatus(buildingId);
          const isTerminal = data.status === 'done' || data.status === 'error';
          const isFresh =
            isTerminal &&
            data.finishedAt != null &&
            data.finishedAt !== previousFinishedAtRef.current;
          if (!isFresh) {
            // Postęp w toku: pokazujemy kroki na bieżąco, ale NIE kończymy pollingu.
            if (data.status === 'running') {
              setResult(data);
              setStatus('running');
            }
            return;
          }
          previousFinishedAtRef.current = data.finishedAt;
          clearPoll();
          setResult(data);
          setStatus(data.status as CeebPhaseAUiStatus);
        } catch (_) {
          // Błąd pojedynczego polla (sieć) — kolejny tick spróbuje ponownie.
        }
      }, POLL_INTERVAL_MS);
    },
    [clearPoll]
  );

  const start = useCallback(
    async (buildingId: string) => {
      clearPoll();
      setStatus('running');
      setResult(null);
      startedAtRef.current = Date.now();

      try {
        const baseline = await buildingsApi.getPhaseAStatus(buildingId);
        previousFinishedAtRef.current = baseline?.finishedAt ?? undefined;
      } catch (_) {
        previousFinishedAtRef.current = undefined;
      }

      try {
        await buildingsApi.startPhaseA(buildingId);
      } catch (e: any) {
        clearPoll();
        setResult({ status: 'error', error: e?.message || 'Nie udało się uruchomić Fazy A' });
        setStatus('error');
        return;
      }

      startPolling(buildingId);
    },
    [clearPoll, startPolling]
  );

  const observe = useCallback(
    async (buildingId: string) => {
      let data: PhaseAStatusResponse;
      try {
        data = await buildingsApi.getPhaseAStatus(buildingId);
      } catch (_) {
        return; // brak statusu nie jest błędem karty budynku
      }
      setResult(data.status === 'idle' ? null : data);
      setStatus(data.status as CeebPhaseAUiStatus);
      // Przebieg trwa (np. wystartował w innej karcie) — dołączamy się do pollingu,
      // żeby pasek postępu żył także po odświeżeniu strony.
      if (data.status === 'running') {
        previousFinishedAtRef.current = undefined;
        startedAtRef.current = Date.now();
        startPolling(buildingId);
      }
    },
    [startPolling]
  );

  return { status, result, start, observe };
}
